import { ChevronDown, X } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useWeekRangeCalendar } from '@/features/purchase-planning/hooks/use-week-range-calendar';
import { PurchasePlanningCalendar } from './purchase-planning-calendar';
import { CtaCalendarActionSkeleton } from './skeleton/cta-calendar-action';
import { formatWeekRangeLabel } from './utils/format-week-range-label';

export function PurchasePlanningCollapseCalendar() {
  const { t, ready } = useTranslation(['purchasePlanning', 'common']);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { dateRange, handleSelect, reset } = useWeekRangeCalendar();

  const label = formatWeekRangeLabel(dateRange, {
    week: t('common:week'),
    showCalendar: t('common:showCalendar'),
  });

  if (!ready) {
    return <CtaCalendarActionSkeleton />;
  }

  const handleClearSelection = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reset();
  };

  return (
    <Collapsible className='space-y-4' onOpenChange={setIsOpen} open={isOpen}>
      <CollapsibleTrigger asChild>
        <div className='flex w-fit space-x-4'>
          <ButtonGroup>
            <Button variant='outline'>
              {label}
              <motion.span
                animate={{ rotate: isOpen ? 180 : 0 }}
                className='inline-flex'
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                <ChevronDown className='h-4 w-4' />
              </motion.span>
            </Button>
            <Button
              onClick={handleClearSelection}
              size='icon'
              variant='outline'
            >
              <X className='h-4 w-4' />
            </Button>
          </ButtonGroup>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent asChild forceMount>
        <motion.div
          animate={{
            opacity: isOpen ? 1 : 0,
            height: isOpen ? 'auto' : 0,
          }}
          className='md:relative md:-left-[248px]'
          initial={false}
          style={{ overflow: 'hidden' }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        >
          <PurchasePlanningCalendar
            dateRange={dateRange}
            handleSelect={handleSelect}
          />
        </motion.div>
      </CollapsibleContent>
    </Collapsible>
  );
}
